import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Pagination,
  Tooltip,
  Button
} from '@mui/material'
import { useEffect, useState } from 'react'
import '../../Style/Pulse.css'
import { AddComment, Close, Delete, Edit } from '@mui/icons-material'
import CommentTypeIcon from '../View/CommentTypeIcon'
import CommentEditModal from './CommentEditModal'
import noData from '../../Icons/noData.jpg'
import RTEView from '../RichTextEditor/RTEView'

const PulseComments = ({
  comments,
  setComments,
  pulseValues,
  isAllowedToComment,
  uid,
  document,
  isAdmin,
  sectionIndex,
  pulseIndex,
  sortType,
  setSortType,
  prompt,
  setAlert,
  toggleDrawer,
  showEditModal
}) => {
  const [visibleComments, setVisibleComments] = useState([])
  const [page, setPage] = useState(1)
  const [comment, setComment] = useState(null)
  const [showEdit, setShowEdit] = useState(false)

  const handleSortType = event => {
    setSortType(event.target.value)
  }

  function handleVisibleComments () {
    let v = comments
      .filter(
        val =>
          (val.option === sortType ||
            sortType === 'All Comments' ||
            (val.uid === uid && sortType === 'Yours')) &&
          val.sectionIndex === sectionIndex &&
          val.pulseIndex === pulseIndex
      )
      .filter(val => {
        if (!document.commentVisibility) {
          return val.uid === uid || isAdmin
        } else {
          return true
        }
      })
      .filter(val => {
        if (sortType === 'All Comments') {
          return val.message.length > 0 || val.uid === uid
        } else {
          return true
        }
      })
      .sort((a, b) => {
        return b.time - a.time
      })
    setVisibleComments(v)
  }

  useEffect(() => {
    handleVisibleComments()
    setPage(1)
  }, [comments, sortType])

  const handlePageChange = (_e, value) => {
    setPage(value)
  }

  const removeComment = currentComment => {
    if (!isAllowedToComment) {
      return
    }
    if (uid !== currentComment.uid && !isAdmin) {
      console.warn('User is not allowed to remove this comment.')
      return
    }
    if (uid === currentComment.uid && !isAdmin) {
      toggleDrawer(false)
    }
    setComments(
      comments.filter(val => val.id !== currentComment.id),
      currentComment,
      false
    )
  }

  const findYourComment = () => {
    return comments.find(
      val =>
        val.uid === uid &&
        val.sectionIndex === sectionIndex &&
        val.pulseIndex === pulseIndex
    )
  }

  const handleOpenEdit = () => {
    let c = findYourComment()
    if (c) {
      setComment(c)
      setShowEdit(true)
    }
  }

  const handleEditClick = currentComment => {
    setComment(currentComment)
    setShowEdit(true)
  }

  const handleEditResponse = currentComment => {
    let c = { ...comment }
    setComment(null)
    setShowEdit(false)
    setComments(
      [...comments.filter(val => val.id !== c.id), currentComment],
      comment,
      false,
      () =>
        setComments(
          [...comments.filter(val => val.id !== c.id), currentComment],
          currentComment,
          true
        )
    )
  }

  useEffect(() => {
    if (showEditModal) {
      handleEditClick(findYourComment())
    }
  }, [showEditModal])

  const MessageItem = ({ message }) => {
    const [showFull, setShowFull] = useState(false)
    const toggleShowFull = e => {
      e.stopPropagation()
      setShowFull(!showFull)
    }
    if (message.length <= 100) {
      return <span>{message}</span>
    } else {
      return (
        <span>
          {showFull ? message : message.substring(0, 100) + '... '}
          <br />
          <Button onClick={toggleShowFull} size='small'>
            {showFull ? 'Show Less' : 'Show More'}
          </Button>
        </span>
      )
    }
  }

  const ActionButtonBar = ({
    handleOpenEdit,
    toggleDrawer,
    isAllowedToComment,
    findYourComment
  }) => {
    return (
      <Stack
        direction='row'
        spacing={1}
        justifyContent={
          isAllowedToComment && findYourComment() ? 'space-between' : 'flex-end'
        }
      >
        {isAllowedToComment && findYourComment() && (
          <Tooltip title='Add or Edit Your Response'>
            <Button size='small' variant='contained' onClick={handleOpenEdit}>
              <AddComment />
            </Button>
          </Tooltip>
        )}
        <IconButton onClick={() => toggleDrawer(false)}>
          <Close />
        </IconButton>
      </Stack>
    )
  }

  const CommentPagination = ({ id }) => {
    return (
      <Pagination
        id={id}
        count={Math.ceil(visibleComments.length / 10)}
        onChange={handlePageChange}
        page={page}
        color='primary'
        sx={{ paddingTop: 1, marginTop: 'auto' }}
      />
    )
  }

  return (
    <Box
      className='pulse-comments'
      sx={{
        padding: 1
      }}
    >
      <ActionButtonBar
        handleOpenEdit={handleOpenEdit}
        toggleDrawer={toggleDrawer}
        isAllowedToComment={isAllowedToComment}
        findYourComment={findYourComment}
      />
      <h4 style={{ overflowWrap: 'anywhere' }}>{prompt}</h4>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        spacing={1}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={1}
        >
          <h5>{'Responses (' + visibleComments.length + ')'}</h5>
        </Stack>
        <TextField
          id='sortAdminComments'
          select
          label='Sort/Filter'
          defaultValue='Comment'
          variant='outlined'
          sx={{
            minWidth: '100px',
            maxWidth: '300px',
            pr: 1
          }}
          value={sortType}
          onChange={handleSortType}
          size='small'
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  maxWidth: 500
                }
              }
            }
          }}
        >
          {['All Comments', ...pulseValues, 'Yours'].map(option => (
            <MenuItem key={option} value={option}>
              <Box sx={{ display: 'inline', width: '100%' }}>
                <RTEView content={option} cut />
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Box className='comment-input' sx={{ p: 1 }}>
        <Stack spacing={1} key={sortType} className='bmSib'>
          {visibleComments
            .slice((page - 1) * 10, page * 10)
            .map(currentComment => (
              <Card
                raised
                sx={{ p: 1 }}
                key={currentComment.id + currentComment.uid + sortType}
                onClick={e => {
                  if (isAllowedToComment && uid === currentComment.uid) {
                    handleEditClick(currentComment)
                  }
                }}
                className={
                  isAllowedToComment && uid === currentComment.uid
                    ? 'pointer'
                    : ''
                }
              >
                <CardHeader
                  avatar={
                    <CommentTypeIcon
                      type={
                        currentComment.message.length > 0 ? 'Comment' : 'Vote'
                      }
                      currentComment={currentComment}
                      document={document}
                      uid={uid}
                    />
                  }
                  title={
                    <span style={{ display: 'inline' }}>
                      {(currentComment.displayName
                        ? currentComment.displayName
                        : currentComment.uid) +
                        (currentComment.message.length > 0 ? ' - ' : '')}
                      {currentComment.message.length > 0 && (
                        <RTEView
                          content={currentComment.option}
                          fontSize='0.875rem'
                          cut
                        />
                      )}
                    </span>
                  }
                  subheader={currentComment.dateString}
                  action={
                    <Stack direction='row'>
                      {isAllowedToComment && uid === currentComment.uid && (
                        <Tooltip title='Add or Edit Your Response'>
                          <IconButton
                            onClick={() => {
                              handleEditClick(currentComment)
                            }}
                            variant='outlined'
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isAllowedToComment &&
                        (uid === currentComment.uid ||
                          uid === document.uid ||
                          (document.admins &&
                            document.admins.includes(uid))) && (
                          <Tooltip title='Remove Response'>
                            <IconButton
                              onClick={e => {
                                e.stopPropagation()
                                removeComment(currentComment)
                                setAlert(null)
                              }}
                              color='error'
                              variant='outlined'
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Stack>
                  }
                  sx={{
                    flexWrap: 'wrap',
                    '.MuiCardHeader-action': {
                      marginLeft: 'auto',
                      padding: '8px'
                    },
                    padding: 0
                  }}
                />
                <CardContent sx={{ padding: 1, paddingBottom: '0 !important' }}>
                  <span style={{ fontWeight: '600', wordBreak: 'break-word' }}>
                    {currentComment.message.length > 0 ? (
                      <MessageItem message={currentComment.message} />
                    ) : (
                      <RTEView
                        fontWeight={'600'}
                        content={currentComment.option}
                        cut
                      />
                    )}
                  </span>
                </CardContent>
              </Card>
            ))}
        </Stack>
        <div className='bm' style={{ display: 'none', textAlign: 'center' }}>
          <img
            src={noData}
            alt='No Comments'
            style={{ borderRadius: '4px', width: '100%' }}
          />

          {sortType === 'All Comments'
            ? 'No comments on this prompt yet.'
            : 'No comments on this prompt with selected filter.'}
        </div>
      </Box>
      {visibleComments.length > 10 && (
        <>
          <CommentPagination id='commentPagination2' />
          <ActionButtonBar
            handleOpenEdit={handleOpenEdit}
            toggleDrawer={toggleDrawer}
            isAllowedToComment={isAllowedToComment}
            findYourComment={findYourComment}
          />
        </>
      )}

      {showEdit && (
        <CommentEditModal
          showModal={showEdit}
          setShowModal={setShowEdit}
          addComment={handleEditResponse}
          currentComment={comment}
          prompt={prompt}
        />
      )}
    </Box>
  )
}

export default PulseComments
